import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input, Popover, Select, Table, Tooltip, message } from "antd";
import type { TableColumnsType } from "antd";
import { ArrowLeft, FolderOpen, History, Pencil, Plus, Search, Sparkles, Trash2, Eye, EyeOff, Layers, Link2 } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import LoadingOverlay from "components/loading-overlay/loading-overlay";
import PlanReviewRuleModal, {
	PlanReviewRuleFormValues
} from "components/plan-review-rule-modal/plan-review-rule-modal";
import PlanReviewExtractModal from "components/plan-review-extract-modal/plan-review-extract-modal";
import CodeSectionPanel, {
	CodeSectionReference,
	crossReferenceToCodeSectionReference
} from "components/code-section-panel/code-section-panel";

import { useGetPlanReviewRulesQuery } from "graphql/queries/get-plan-review-rules.generated";
import { useGetPlanReviewCrossReferencesQuery } from "graphql/queries/get-plan-review-cross-references.generated";
import { useGetPlanReviewSubmissionTypesQuery } from "graphql/queries/get-plan-review-submission-types.generated";
import { useInsertPlanReviewRequirementMutation } from "graphql/mutations/insert-plan-review-requirement.generated";
import { useUpdatePlanReviewRequirementMutation } from "graphql/mutations/update-plan-review-requirement.generated";
import { useSetPlanReviewRequirementActiveMutation } from "graphql/mutations/set-plan-review-requirement-active.generated";
import { useDeletePlanReviewRequirementMutation } from "graphql/mutations/delete-plan-review-requirement.generated";
import { useSetPlanReviewRequirementCodeSectionsMutation } from "graphql/mutations/set-plan-review-requirement-code-sections.generated";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { usePlanReviewJurisdiction } from "hooks/usePlanReviewJurisdiction";
import { usePlanReviewCategories } from "hooks/usePlanReviewCategories";
import { usePlanReviewSettingsAccess } from "hooks/usePlanReviewSettingsAccess";
import routeNames from "constants/route-names";
import { PlanReviewRequirementOrigin } from "constants/field-values";
import { PLAN_REVIEW_REQUIREMENT_TYPE_OPTIONS, PLAN_REVIEW_SEVERITY_OPTIONS } from "constants/constants";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";
import { formatSectionNumberForCitation } from "utils/code-section-citation";

type Rule = NonNullable<ReturnType<typeof useGetPlanReviewRulesQuery>["data"]>["plan_review_requirements"][number];
type RuleCrossReferences = NonNullable<
	ReturnType<typeof useGetPlanReviewCrossReferencesQuery>["data"]
>["get_plan_review_cross_references"][number];
type CrossReferencedSection = RuleCrossReferences["sections"][number];

// 'IFC 2021 § 903.3.1.1' or 'IFC 2021 Appendix D'. Citation chips and cross-reference chips use
// the same text.
const formatCodeSectionLabel = (reference: CodeSectionReference): string => {
	const section = reference.code_section;
	if (!section) return reference.citation_text ?? "Code section";
	const edition = section.code_book?.edition_year ? ` ${section.code_book.edition_year}` : "";
	return `${section.code_book?.abbreviation ?? ""}${edition} ${formatSectionNumberForCitation(section.section_number)}`;
};

interface CodeSectionChipProps {
	reference: CodeSectionReference;
	tooltip: string;
	className: string;
	icon?: ReactNode;
}

/**
 * One code section on a rule row: a section the rule cites, or one that its cited code refers to.
 *
 * Hover shows `tooltip`. Click opens the section text right there, in the same panel as the finding
 * report, with the link to the code PDF.
 *
 * The content is always passed, even while closed: antd refuses to open a popover whose content
 * is empty at the moment of the click. The panel still loads only when opened, because antd mounts
 * the popover content on the first open and destroyTooltipOnHide unmounts it on close.
 */
const CodeSectionChip = ({ reference, tooltip, className, icon }: CodeSectionChipProps) => {
	const [isPanelOpen, setIsPanelOpen] = useState(false);

	return (
		<Popover
			trigger="click"
			placement="bottomLeft"
			rootClassName="prr-code-section-popover"
			open={isPanelOpen}
			onOpenChange={setIsPanelOpen}
			destroyTooltipOnHide
			content={<CodeSectionPanel references={[reference]} defaultExpanded />}
		>
			{/* Hidden while the panel is open: the panel already shows the section. */}
			<Tooltip title={tooltip} open={isPanelOpen ? false : undefined}>
				<button type="button" className={className}>
					{icon}
					{formatCodeSectionLabel(reference)}
				</button>
			</Tooltip>
		</Popover>
	);
};

// Rule id → the sections its cited code refers to. A rule without cross-references is not in the map.
const groupCrossReferencesByRule = (
	rulesCrossReferences: RuleCrossReferences[]
): Map<string, CrossReferencedSection[]> => {
	const sectionsByRuleId = new Map<string, CrossReferencedSection[]>();
	for (const ruleCrossReferences of rulesCrossReferences) {
		sectionsByRuleId.set(ruleCrossReferences.requirement_id, ruleCrossReferences.sections);
	}
	return sectionsByRuleId;
};

const SEVERITY_CLASS: Record<string, string> = {
	CRITICAL: "prr-badge-critical",
	HIGH: "prr-badge-high",
	MEDIUM: "prr-badge-medium",
	INFO: "prr-badge-info"
};

// A citation is one string written by hand and another written by the picker — "IFC
// 2021 507.5.1" and "IFC 2021 § 507.5.1" name the same section — so they are compared
// with the punctuation an author varies freely taken out.
const citationKey = (citation: string): string =>
	citation
		.toLowerCase()
		.replace(/§|\bsections?\b|\bsec\b/g, " ")
		.replace(/[^a-z0-9.]+/g, " ")
		// A period between digits is a level of the section number; anywhere else it is
		// punctuation the author happened to type.
		.replace(/\.(?!\d)/g, " ")
		.replace(/\s+/g, " ")
		.trim();

const toFormValues = (rule: Rule): PlanReviewRuleFormValues => {
	const codeCitations = rule.plan_review_requirement_code_sections.map(link => ({
		code_section_id: link.code_section_id,
		citation_text: link.citation_text ?? "",
		origin: link.origin,
		label: link.code_section ? formatCodeSectionLabel(link) : (link.citation_text ?? "")
	}));

	// The picker and the text field used to hold the same citations, side by side, with
	// no sign that they were the same thing: emptying the text to let the picker
	// "replace" it deleted what the picker's own links are rebuilt from. So the text
	// field holds what the picker does not — the codes with no section on file — and
	// the two are put back together on save.
	const covered = new Set(codeCitations.map(citation => citationKey(citation.citation_text)).filter(Boolean));

	return {
		id: rule.id,
		requirement_type: rule.requirement_type,
		category_id: rule.category_id,
		code: rule.code ?? "",
		title: rule.title,
		description: rule.description,
		applicable_codes: (Array.isArray(rule.applicable_codes) ? rule.applicable_codes : [])
			.filter(code => !covered.has(citationKey(String(code))))
			.join(", "),
		codeCitations,
		source: rule.source ?? "",
		section: rule.section ?? "",
		source_url: rule.source_url ?? "",
		default_severity: rule.default_severity ?? "MEDIUM",
		required: rule.required,
		sort_order: rule.sort_order,
		submissionTypeIds: rule.plan_review_requirement_submission_types.map(t => t.submission_type_id)
	};
};

// The form keeps applicable codes as one comma-separated line; the column is jsonb.
const parseApplicableCodes = (raw: string): string[] =>
	raw
		.split(",")
		.map(code => code.trim())
		.filter(Boolean);

/**
 * The AHJ's rules container: everything a submittal to this jurisdiction is screened
 * against. Every rule belongs to the jurisdiction and is fully editable — there is no
 * shared baseline a manager cannot touch.
 */
const PlanReviewRules = () => {
	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();
	const { jurisdictionId, picker, emptyReason } = usePlanReviewJurisdiction();
	// Tells Ollie what the reader is looking at, and for which authority.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The plan review rules screen: the requirements this authority checks every submittal against, each one either a SUBMITTAL rule (paperwork that must be provided) or a CODE rule (a fire or life-safety rule the plans must satisfy). The reader maintains them here.",
			jurisdictionId
		}),
		{ jurisdictionId }
	);

	const { canManage, blockChange, notice } = usePlanReviewSettingsAccess();
	// Appended to every control that writes: it reads as unavailable but still answers.
	const lockedClass = canManage ? "" : " prr-locked";

	// Arriving from a submittal type on the Submission Types screen opens the screen
	// already narrowed to that type's rules.
	const [searchParams] = useSearchParams();

	const [search, setSearch] = useState("");
	const [categoryFilter, setCategoryFilter] = useState<string>("all");
	const [typeFilter, setTypeFilter] = useState<string>("all");
	const [submissionTypeFilter, setSubmissionTypeFilter] = useState<string>(searchParams.get("submissionType") || "all");

	const [editing, setEditing] = useState<PlanReviewRuleFormValues | null>(null);
	const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
	const [isExtractModalOpen, setIsExtractModalOpen] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);

	const {
		data,
		loading,
		error: loadError,
		refetch
	} = useGetPlanReviewRulesQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-and-network"
	});

	const { data: typesData } = useGetPlanReviewSubmissionTypesQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-and-network"
	});
	const submissionTypes = useMemo(() => typesData?.plan_review_submission_types ?? [], [typesData]);

	// The backend reads them from the text of each rule's cited sections, so they change when a
	// rule's citations change. That is why a save refetches them too.
	const { data: crossReferencesData, refetch: refetchCrossReferences } = useGetPlanReviewCrossReferencesQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-and-network"
	});
	const crossReferencedSectionsByRuleId = useMemo(
		() => groupCrossReferencesByRule(crossReferencesData?.get_plan_review_cross_references ?? []),
		[crossReferencesData]
	);

	const { categoryOptions } = usePlanReviewCategories(jurisdictionId);

	// A type filter that survived a jurisdiction change belongs to the previous AHJ, and
	// would leave the screen empty with a filter showing an id nobody can read.
	useEffect(() => {
		if (submissionTypeFilter === "all" || submissionTypes.length === 0) return;
		if (!submissionTypes.some(type => type.id === submissionTypeFilter)) setSubmissionTypeFilter("all");
	}, [submissionTypes, submissionTypeFilter]);

	const [insertRule] = useInsertPlanReviewRequirementMutation();
	const [updateRule] = useUpdatePlanReviewRequirementMutation();
	const [setActive] = useSetPlanReviewRequirementActiveMutation();
	const [deleteRule] = useDeletePlanReviewRequirementMutation();
	const [setCodeSections] = useSetPlanReviewRequirementCodeSectionsMutation();

	const rules = useMemo(() => data?.plan_review_requirements ?? [], [data]);

	const filtered = useMemo(() => {
		const term = search.trim().toLowerCase();
		return rules.filter(rule => {
			const matchesSearch =
				!term ||
				rule.title.toLowerCase().includes(term) ||
				rule.description.toLowerCase().includes(term) ||
				(rule.code ?? "").toLowerCase().includes(term);
			const matchesCategory = categoryFilter === "all" || rule.category_id === categoryFilter;
			const matchesType = typeFilter === "all" || rule.requirement_type === typeFilter;
			const matchesSubmissionType =
				submissionTypeFilter === "all" ||
				rule.plan_review_requirement_submission_types.some(t => t.submission_type_id === submissionTypeFilter);
			return matchesSearch && matchesCategory && matchesType && matchesSubmissionType;
		});
	}, [rules, search, categoryFilter, typeFilter, submissionTypeFilter]);

	const handleAdd = useCallback(() => {
		if (!canManage) return blockChange();
		setEditing(null);
		setSaveError(null);
		setIsRuleModalOpen(true);
	}, [canManage, blockChange]);

	const handleEdit = useCallback(
		(rule: Rule) => {
			if (!canManage) return blockChange();
			setEditing(toFormValues(rule));
			setSaveError(null);
			setIsRuleModalOpen(true);
		},
		[canManage, blockChange]
	);

	const handleExtract = useCallback(() => {
		if (!canManage) return blockChange();
		setIsExtractModalOpen(true);
	}, [canManage, blockChange]);

	const handleSave = useCallback(
		async (values: PlanReviewRuleFormValues) => {
			setIsSaving(true);
			setSaveError(null);
			try {
				// The picker writes the text too. applicable_codes stays the rule's whole
				// list of citations — it is what the report prints and what the citation
				// backfill resolves from — and the field beside the picker only ever holds
				// the ones with no section on file.
				const picked = values.codeCitations.map(citation => citation.citation_text.trim()).filter(Boolean);
				const pickedKeys = new Set(picked.map(citationKey));
				const applicableCodes = [
					...picked,
					...parseApplicableCodes(values.applicable_codes).filter(code => !pickedKeys.has(citationKey(code)))
				];

				const fields = {
					requirement_type: values.requirement_type,
					category_id: values.category_id,
					code: values.code.trim() || null,
					title: values.title.trim(),
					description: values.description.trim(),
					applicable_codes: applicableCodes,
					source: values.source.trim() || null,
					section: values.section.trim() || null,
					source_url: values.source_url.trim() || null,
					default_severity: values.default_severity,
					required: values.required,
					sort_order: values.sort_order
				};

				let requirementId = values.id;

				if (values.id) {
					await updateRule({
						variables: {
							id: values.id,
							set: fields,
							submissionTypes: values.submissionTypeIds.map(submission_type_id => ({
								requirement_id: values.id,
								submission_type_id
							}))
						}
					});
				} else {
					// Nested insert: the rule and its types have to commit together or the
					// deferred constraint rejects the rule.
					const { data: created } = await insertRule({
						variables: {
							object: {
								...fields,
								jurisdiction_id: jurisdictionId,
								origin: PlanReviewRequirementOrigin.MANUAL,
								created_by_user_id: loggedUserValue?.id,
								plan_review_requirement_submission_types: {
									data: values.submissionTypeIds.map(submission_type_id => ({ submission_type_id }))
								}
							}
						}
					});
					requirementId = created?.insert_plan_review_requirements_one?.id;
				}

				// Written after the rule so a new one has an id to hang them on. Rewritten
				// whole, including the ones an extraction resolved, because the modal held
				// the complete list while the manager edited it. A new rule with no
				// citations has nothing to write and nothing to clear.
				if (requirementId && (values.id || values.codeCitations.length > 0)) {
					await setCodeSections({
						variables: {
							requirementId,
							objects: values.codeCitations.map((citation, index) => ({
								requirement_id: requirementId,
								code_section_id: citation.code_section_id,
								citation_text: citation.citation_text || null,
								origin: citation.origin,
								sort_order: index
							}))
						}
					});
				}

				await Promise.all([refetch(), refetchCrossReferences()]);
				setIsRuleModalOpen(false);
				message.success(values.id ? "Rule updated." : "Rule created.");
			} catch (error) {
				console.error("Plan review rule save failed:", error);
				setSaveError(error instanceof Error ? error.message : "We couldn't save the rule. Please try again.");
			} finally {
				setIsSaving(false);
			}
		},
		[insertRule, updateRule, setCodeSections, refetch, refetchCrossReferences, jurisdictionId, loggedUserValue]
	);

	const handleToggleActive = useCallback(
		async (rule: Rule) => {
			if (!canManage) return blockChange();
			try {
				await setActive({ variables: { id: rule.id, active: !rule.active } });
				await refetch();
			} catch (error) {
				console.error("Plan review rule activation failed:", error);
				message.error("We couldn't change the rule. Please try again.");
			}
		},
		[setActive, refetch, canManage, blockChange]
	);

	const handleDelete = useCallback(
		async (rule: Rule) => {
			if (!canManage) return blockChange();
			try {
				await deleteRule({ variables: { id: rule.id } });
				await refetch();
				message.success("Rule deleted.");
			} catch (error) {
				console.error("Plan review rule delete failed:", error);
				message.error("We couldn't delete the rule. It may be referenced by an existing review.");
			}
		},
		[deleteRule, refetch, canManage, blockChange]
	);

	const columns: TableColumnsType<Rule> = useMemo(
		() => [
			{
				title: "Rule",
				key: "rule",
				render: (_, rule) => (
					<div>
						<p className="prr-cell-title">{rule.title}</p>
						<p className="prr-cell-sub">{rule.description}</p>
						<div className="prr-badge-list" style={{ marginTop: 6 }}>
							{rule.code ? <span className="prr-code">{rule.code}</span> : null}
							{rule.origin === PlanReviewRequirementOrigin.AI_EXTRACTION ? (
								<span className="prr-badge prr-badge-ai">AI</span>
							) : null}
							{/* The code behind the rule, which is what the analysis reads with it. */}
							{rule.plan_review_requirement_code_sections.map(link => (
								<CodeSectionChip
									key={link.code_section_id}
									reference={link}
									tooltip="Cited by this rule. Click to read it."
									className="prr-badge prr-badge-code"
								/>
							))}
							{/* The sections that the cited code refers to. The analysis reads them with the rule. */}
							{crossReferencedSectionsByRuleId.get(rule.id)?.map(section => (
								<CodeSectionChip
									key={section.code_section_id}
									reference={crossReferenceToCodeSectionReference(section)}
									tooltip={`Cross-referenced by ${section.referenced_by.join(", ")}. Click to read it.`}
									className="prr-badge prr-badge-cross-reference"
									icon={<Link2 size={12} />}
								/>
							))}
						</div>
					</div>
				)
			},
			{
				title: "Category",
				dataIndex: ["category_record", "name"],
				key: "category",
				width: 200,
				render: (category: string) => <span className="prr-badge prr-badge-neutral">{category}</span>
			},
			{
				title: "Submittals",
				key: "submission_types",
				width: 190,
				render: (_, rule) => (
					<div className="prr-badge-list">
						{rule.plan_review_requirement_submission_types.map(link => (
							<span key={link.submission_type_id} className="prr-badge prr-badge-neutral">
								{link.submission_type?.short_name ?? link.submission_type?.name}
							</span>
						))}
					</div>
				)
			},
			{
				title: "Severity",
				dataIndex: "default_severity",
				key: "default_severity",
				width: 110,
				render: (severity: string | null) =>
					severity ? (
						<span className={`prr-badge ${SEVERITY_CLASS[severity] ?? "prr-badge-neutral"}`}>{severity}</span>
					) : null
			},
			{
				title: "",
				key: "actions",
				width: 120,
				render: (_, rule) => (
					<div className="prr-cell-actions">
						<button
							type="button"
							className={`prr-icon-btn${lockedClass}`}
							title="Edit"
							aria-disabled={!canManage}
							onClick={() => handleEdit(rule)}
						>
							<Pencil size={15} />
						</button>
						<button
							type="button"
							className={`prr-icon-btn${lockedClass}`}
							title={rule.active ? "Deactivate" : "Reactivate"}
							aria-disabled={!canManage}
							onClick={() => handleToggleActive(rule)}
						>
							{rule.active ? <EyeOff size={15} /> : <Eye size={15} />}
						</button>
						<button
							type="button"
							className={`prr-icon-btn${lockedClass}`}
							title="Delete"
							aria-disabled={!canManage}
							onClick={() => handleDelete(rule)}
						>
							<Trash2 size={15} />
						</button>
					</div>
				)
			}
		],
		[handleEdit, handleToggleActive, handleDelete, canManage, lockedClass, crossReferencedSectionsByRuleId]
	);

	return (
		<>
			<OllieLayout>
				<div className="prr-page">
					<div className="prr-shell">
						<header className="prr-header">
							<div>
								<h1 className="prr-title">Plan Review Rules</h1>
								<p className="prr-subtitle">
									What every submittal to your jurisdiction is screened against. These rules are yours: edit, deactivate
									or delete any of them.
								</p>
							</div>
							<div className="prr-header-actions">
								<button className="prr-btn" onClick={() => navigate(routeNames.PLAN_REVIEW_SUBMISSION_TYPES)}>
									<Layers size={16} />
									Submission types
								</button>
								<button className="prr-btn" onClick={() => navigate(routeNames.PLAN_REVIEW_EXTRACTIONS)}>
									<History size={16} />
									Extractions
								</button>
								<button className="prr-btn" onClick={() => navigate(routeNames.PLAN_REVIEW_RESOURCES)}>
									<FolderOpen size={16} />
									Resources
								</button>
								<button
									className={`prr-btn${lockedClass}`}
									onClick={handleExtract}
									aria-disabled={!canManage}
									disabled={!jurisdictionId}
								>
									<Sparkles size={16} />
									Generate with AI
								</button>
								<button
									className={`prr-btn prr-btn-primary${lockedClass}`}
									onClick={handleAdd}
									aria-disabled={!canManage}
									disabled={!jurisdictionId}
								>
									<Plus size={16} />
									Add rule
								</button>
							</div>
						</header>

						{/* A platform admin belongs to no AHJ and has to choose one. It sits above the
						    empty state that asks for it, and outside the filters row, which is hidden
						    until a jurisdiction is picked. */}
						{picker ? <div className="prr-jurisdiction">{picker}</div> : null}

						{notice}

						{loadError ? (
							<div className="prr-empty">
								<p className="prr-empty-title">We couldn&apos;t load the rules</p>
								<p>{loadError.message}</p>
							</div>
						) : emptyReason ? (
							<div className="prr-empty">
								<p className="prr-empty-title">{emptyReason.title}</p>
								<p>{emptyReason.detail}</p>
							</div>
						) : null}

						<div className="prr-filters" style={loadError || emptyReason ? { display: "none" } : undefined}>
							<div className="prr-filter prr-filter-grow">
								<label className="prr-label">Search</label>
								<Input
									className="prr-control"
									placeholder="Title, description or code…"
									prefix={<Search size={16} color="#90a1b9" />}
									value={search}
									onChange={e => setSearch(e.target.value)}
								/>
							</div>
							<div className="prr-filter">
								<label className="prr-label">Category</label>
								<Select
									className="prr-control"
									value={categoryFilter}
									onChange={setCategoryFilter}
									options={[{ label: "All categories", value: "all" }, ...categoryOptions]}
								/>
							</div>
							<div className="prr-filter">
								<label className="prr-label">Type</label>
								<Select
									className="prr-control"
									value={typeFilter}
									onChange={setTypeFilter}
									options={[
										{ label: "All types", value: "all" },
										...PLAN_REVIEW_REQUIREMENT_TYPE_OPTIONS.map(o => ({
											label: o.label,
											value: o.value
										}))
									]}
								/>
							</div>
							<div className="prr-filter">
								<label className="prr-label">Submission type</label>
								<Select
									className="prr-control"
									value={submissionTypeFilter}
									onChange={setSubmissionTypeFilter}
									options={[
										{ label: "All submittals", value: "all" },
										...submissionTypes.map(t => ({ label: t.name, value: t.id }))
									]}
								/>
							</div>
						</div>

						{loadError || emptyReason ? null : !loading && filtered.length === 0 ? (
							<div className="prr-empty">
								<p className="prr-empty-title">No rules match your filters</p>
								<p>Add one by hand, or let Ollie read your submittal guidelines and propose them.</p>
							</div>
						) : (
							<Table
								className="prr-table"
								rowKey="id"
								columns={columns}
								dataSource={filtered}
								loading={loading && rules.length === 0}
								pagination={{ pageSize: 25, hideOnSinglePage: true }}
								rowClassName={rule => (rule.active ? "" : "prr-row-inactive")}
							/>
						)}
					</div>
				</div>
			</OllieLayout>

			<PlanReviewRuleModal
				open={isRuleModalOpen}
				jurisdictionId={jurisdictionId}
				initialValues={editing}
				submissionTypes={submissionTypes}
				categoryOptions={categoryOptions}
				saving={isSaving}
				error={saveError}
				onCancel={() => setIsRuleModalOpen(false)}
				onSave={handleSave}
			/>

			<PlanReviewExtractModal
				open={isExtractModalOpen}
				jurisdictionId={jurisdictionId}
				submissionTypes={submissionTypes}
				onCancel={() => setIsExtractModalOpen(false)}
				onStarted={extractionId =>
					navigate(routeNames.PLAN_REVIEW_RULES_EXTRACTION.replace(":extractionId", extractionId))
				}
			/>

			<LoadingOverlay isLoading={isSaving} tip="Saving rule…" />
		</>
	);
};

export default PlanReviewRules;
